import { Controller } from "@hotwired/stimulus"

// A word search on top of a list (Kloepfer K8-22, 2026-10-10: every list in
// Company Settings → Timesheets - the people lists, the per-person table,
// Locations, Holidays). Every word typed has to be in the row, accents and
// capitals ignored. Rows are the elements marked data-search-row inside the
// list target(s); their words come from data-search-text, else their text.
// Rows another controller draws later (Locations, Holidays) are filtered as
// they come in. The words for "3 of 40" and "Nothing matches" ride on the box
// (timesheets/_list_search_box). Codes & Lists has its own search in
// time_lists_controller.js.
const fold = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

export default class extends Controller {
  static targets = ["input", "list", "count"]

  connect() {
    this._observer = new MutationObserver(() => this._apply())
    this.listTargets.forEach((list) => this._observer.observe(list, { childList: true, subtree: true }))
    this._apply()
  }

  disconnect() {
    this._observer?.disconnect()
    this._none?.remove()
    this._none = null
  }

  filter() {
    this._apply()
  }

  // Escape empties the search first; an empty search lets Escape do what it did before.
  clear(e) {
    if (!this.inputTarget.value) return
    e.stopPropagation()
    this.inputTarget.value = ""
    this._apply()
  }

  // Enter in the box never submits or toggles what's around it.
  keep(e) {
    e.preventDefault()
  }

  _apply() {
    const box = this.hasInputTarget ? this.inputTarget : null
    const q = box ? box.value : ""
    const words = fold(q).split(/\s+/).filter(Boolean)
    let shown = 0
    let total = 0
    this.listTargets.forEach((list) => list.querySelectorAll("[data-search-row]").forEach((row) => {
      total++
      const ok = !words.length || words.every((w) => fold(row.dataset.searchText ?? row.textContent).includes(w))
      // style, not the hidden attribute: a row's own flex class would win over [hidden].
      row.style.display = ok ? "" : "none"
      if (ok) shown++
    }))
    if (this.hasCountTarget) {
      this.countTarget.textContent = words.length ? (box?.dataset.countText || "").replace("%{shown}", shown).replace("%{total}", total) : ""
    }
    this._showNone(words.length && total && !shown ? (box?.dataset.noneText || "").replace("%{q}", q.trim()) : "")
  }

  // "Nothing matches" goes after the last list, outside it, so drawing it never re-runs the search.
  _showNone(text) {
    if (!text) {
      this._none?.remove()
      this._none = null
      return
    }
    if (!this._none) {
      this._none = document.createElement("p")
      this._none.className = "py-2 text-[13px] text-gray-400"
      this.listTargets[this.listTargets.length - 1]?.after(this._none)
    }
    this._none.textContent = text
  }
}
